import { useCallback, useEffect, useRef, useState } from "react";
import { isRecurringActive } from "@/utils/bookingActiveness";
import { bookingTypeWellLabel } from "@/utils/bookingTypeLabel";
import AmenitiesRenderer from "@/components/tpr/AmenityIconRenderer/AmenitiesRenderer";
import ChangeBookingDialog from "@/components/tpr/ChangeBookingDialog/ChangeBookingDialog";
import { CancelBookingDialog } from "@/components/tpr/CancelBookingDialog";
import { SHOW_MEMBER_BOOKING_NOTES } from "@/constants/featureFlags";
import { ManageAmenitiesDialog } from "@/components/tpr/ManageAmenitiesDialog";
import { SlotOccurrencesList } from "@/components/tpr/SlotOccurrencesList/SlotOccurrencesList";
import { RecurrenceExceptionsManager } from "@/components/tpr/RecurrenceExceptions/RecurrenceExceptionsManager";
import Heading from "@/components/tpr/Heading/Heading";
import GroupBookingPill from "@/components/tpr/BookingTypePill/GroupBookingPill";
import RoomGallery from "@/components/tpr/RoomGallery/RoomGallery";
import RoomSwatch from "@/components/tpr/RoomSwatch/RoomSwatch";
import SlotHolidayDialog from "@/components/tpr/SlotHolidayFlow/SlotHolidayDialog";
import { BreadcrumbItem, BreadcrumbLink } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { DisabledFeatureTooltip } from "@/components/tpr/DisabledFeatureTooltip";
import { useBookingActionFlags } from "@/hooks/useBookingActionFlags";
import { defaultItemClasses, secondaryButtonClasses } from "@/constants/ui";
import { useBreadcrumb } from "@/contexts/breadcrumbContext";
import { useMember } from "@/contexts/memberContext";
import { useCommentsQuery } from "@/hooks/useCommentsQuery";
import {
  isMemberAnAdmin,
  isMemberAnOrganisationOwner,
  isMemberAPracticeManager,
} from "@/core/auth";
import { useAddBookingNote } from "@/hooks/useAddBookingNote";
import { useBookingQuery } from "@/hooks/useBookingQuery";
import { decryptForMember } from "@/lib/encryption";
import { formatDateTimeVerbose, formatUtcDate, humanizeDuration } from "@/lib/utils";
import { formatBookingDate, formatGenesisDate, getEffectiveRecurrence, getRecurrenceEndDate, humaniseBookingDate, isBookingFullyInPast, isBookingRepeating } from "@/lib/recurrence";
import { AmenityService } from "@/services/AmenityService";
import { resolveBookedAmenities } from "@/utils/bookedAmenityResolver";
import { getMemberTerminationEndDate } from "@/utils/memberStatus";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock, CalendarX, ChevronLeft, Eye, Save } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import type { Amenity } from "@/types/Amenity";
import type { Booking } from "@/types/Booking";

// Helper function to format recurrence details
const formatRecurrenceDetails = (recurrence: Booking["recurrence"] | Booking["recurrencePattern"]) => {
  if (!recurrence) return null;

  const { frequency, interval, count, until, byWeekday } = recurrence;
  const intervalText = interval && interval > 1 ? `${interval} ` : "";
  const frequencyText = frequency === "daily" ? "day" : frequency === "weekly" ? "week" : "month";
  const pluralFrequency = interval && interval > 1 ? `${frequencyText}s` : frequencyText;

  let pattern = `Every ${intervalText}${pluralFrequency}`;

  if (byWeekday && byWeekday.length > 0) {
    const dayNames = {
      MO: "Monday",
      TU: "Tuesday",
      WE: "Wednesday",
      TH: "Thursday",
      FR: "Friday",
      SA: "Saturday",
      SU: "Sunday",
    };
    const days = byWeekday.map((day) => dayNames[day]).join(", ");
    pattern += ` on ${days}`;
  }

  if (count) {
    pattern += ` `;
  } else if (until) {
    pattern += ` until ${formatUtcDate(new Date(until))}`;
  }

  return pattern;
};



const ViewBooking = () => {
  const { member } = useMember();
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl");
  const safeReturnUrl = returnUrl && returnUrl.startsWith("/") && !returnUrl.startsWith("//") ? returnUrl : null;
  const { setBreadcrumb } = useBreadcrumb();
  const queryClient = useQueryClient();

  // Use React Query hook for fetching booking
  const {
    data: booking,
    isLoading: loading,
    isError,
    error,
  } = useBookingQuery(bookingId || "", {
    enabled: !!bookingId,
  });

  // Use mutation hook for adding notes
  const { mutate: addNote } = useAddBookingNote();

  // The member's OWN booking notes (comments they authored). The backend hard-scopes a non-elevated
  // member to their own comments, and we additionally filter by author so an elevated user viewing
  // this member-facing screen never sees an admin/PM note here (those show on the staff view only).
  const { data: bookingComments } = useCommentsQuery({
    filters: { entityType: "booking", entityId: booking?.id ?? "" },
    enabled: !!booking?.id,
  });
  const myBookingNotes = (bookingComments?.data ?? []).filter((c) => c.authorMemberId === member?.id);

  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [amenitiesLoading, setAmenitiesLoading] = useState(false);
  const [memberNotes, setMemberNotes] = useState("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const [showSlotHolidayDialog, setShowSlotHolidayDialog] = useState(false);
  const [showChangeBookingDialog, setShowChangeBookingDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showManageAmenitiesDialog, setShowManageAmenitiesDialog] = useState(false);
  // Controlled open state + wrapper ref for the "Manage recurrence dates" accordion, so the
  // "Cancel occurrences" button can expand it and scroll the member up to it (steering users
  // away from cancelling the whole series when they only mean to drop a single occurrence).
  const [recurrenceOpen, setRecurrenceOpen] = useState(false);
  const recurrenceRef = useRef<HTMLDivElement>(null);
  const handleCancelOccurrences = useCallback(() => {
    setRecurrenceOpen(true);
    // Wait a frame so the accordion has expanded before scrolling it into view.
    requestAnimationFrame(() => {
      recurrenceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);
  const [changeType, setChangeType] = useState<"datetime" | "duration" | "room" | null>("datetime");
  const actionFlags = useBookingActionFlags();

  // Handler to refresh booking data after slot holiday is added
  const handleSlotHolidaySuccess = useCallback(() => {
    if (bookingId) {
      queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
    }
  }, [bookingId, queryClient]);

  // Handler to refresh booking data after booking change
  const handleBookingChangeSuccess = useCallback(() => {
    if (bookingId) {
      queryClient.refetchQueries({ queryKey: ["booking", bookingId] });
    }
  }, [bookingId, queryClient]);

  if (!bookingId) {
    navigate("/dashboard");
  }

  useEffect(() => {
    setBreadcrumb(
      <>
        <BreadcrumbItem className="text-sm text-gray-600 truncate max-w-[125px] md:max-w-none">
          <BreadcrumbLink href="#">View booking</BreadcrumbLink>
        </BreadcrumbItem>
      </>,
    );
    return () => setBreadcrumb(null);
  }, [setBreadcrumb]);

  // Decrypt notes when booking loads
  useEffect(() => {
    const decryptNotes = async () => {
      if (booking?.memberNotes && member?.userKey) {
        try {
          const decrypted = await decryptForMember(booking.memberNotes, member.userKey);
          setMemberNotes(decrypted);
        } catch (error) {
          console.error("Failed to decrypt notes:", error);
          setMemberNotes("");
        }
      } else {
        setMemberNotes("");
      }
    };
    decryptNotes();
  }, [booking?.memberNotes, member?.userKey]);

  // Fetch attached amenities. Only one location lookup per render, even when several amenities
  // are attached, so a stale reference (booking points at an amenity that no longer exists at
  // the location) doesn't leave the UI stuck on "Loading amenities…".
  useEffect(() => {
    let cancelled = false;
    const fetchAmenities = async () => {
      if (!booking?.bookedAmenities?.length || !booking.locationId) {
        setAmenities([]);
        setAmenitiesLoading(false);
        return;
      }
      setAmenitiesLoading(true);
      try {
        const locationAmenities = await new AmenityService().getAmenitiesForLocation(booking.locationId);
        if (cancelled) return;
        const { resolved, stale } = resolveBookedAmenities(booking.bookedAmenities, locationAmenities);
        if (stale.length > 0) {
          console.warn(
            "[ViewBooking] Stale amenity reference(s) on booking — not resolvable at location",
            stale,
          );
        }
        setAmenities(resolved);
      } catch (error) {
        if (!cancelled) {
          console.error("[ViewBooking] Failed to fetch amenities for location", error);
          setAmenities([]);
        }
      } finally {
        if (!cancelled) setAmenitiesLoading(false);
      }
    };
    fetchAmenities();
    return () => {
      cancelled = true;
    };
  }, [booking?.bookedAmenities, booking?.locationId]);

  const handleSaveNotes = async () => {
    if (!booking || !bookingId) return;

    // Set saving state
    setSaveState("saving");

    try {
      // Use the mutation hook to save notes (encryption happens inside the hook)
      addNote(
        { bookingId, notes: memberNotes },
        {
          onSuccess: () => {
            // Set saved state
            setSaveState("saved");

            // Reset to idle after 1 second
            setTimeout(() => {
              setSaveState("idle");
            }, 1000);
          },
          onError: (error) => {
            console.error("Failed to save notes:", error);
            // Reset to idle on error
            setSaveState("idle");
          },
        },
      );
    } catch (error) {
      console.error("Failed to save notes:", error);
      // Reset to idle on error
      setSaveState("idle");
    }
  };

  // Check if current user is an organisation owner viewing a booking by one of their org members
  const isOrgOwnerViewingOrgMemberBooking = () => {
    if (!member || !booking) return false;

    // Member must be an organisation owner
    if (!isMemberAnOrganisationOwner(member)) return false;

    // Get the organisation ID the current member owns
    const ownedOrgId = member.ownerOfOrganisationId || member.organisation?.id;
    if (!ownedOrgId) return false;

    // Check if the booking member belongs to the same organisation
    const bookingMemberOrgId = booking.member?.organisationId;
    return bookingMemberOrgId === ownedOrgId;
  };

  // Check if the current user can view sensitive booking information
  const canViewSensitiveInfo = () => {
    if (!member || !booking) return false;

    // User is the booking owner
    const isOwner = booking.memberId === member.id;

    // User has privileged role
    const isStaff = isMemberAnAdmin(member) || isMemberAPracticeManager(member);

    // User is an organisation owner viewing a booking by one of their org members
    const isOrgOwnerForBooking = isOrgOwnerViewingOrgMemberBooking();

    return isOwner || isStaff || isOrgOwnerForBooking;
  };

  // Check if the current user can edit/cancel the booking
  const canEditBooking = () => {
    if (!member || !booking) return false;

    // User is the booking owner
    const isOwner = booking.memberId === member.id;

    // User has privileged role
    const isStaff = isMemberAnAdmin(member) || isMemberAPracticeManager(member);

    // User is an organisation owner viewing a booking by one of their org members
    const isOrgOwnerForBooking = isOrgOwnerViewingOrgMemberBooking();

    return isOwner || isStaff || isOrgOwnerForBooking;
  };

  // R19 (hearth/docs/BOOKING_RULES.md#r19) — slot bookings can be moved/room-changed only by
  // elevated users; the server enforces this regardless (403 with reason
  // `slot_move_member_restricted`). This gate hides the button for non-elevated users so they
  // don't hit an avoidable error.
  const canChangeTimeOrRoom = (): boolean => {
    if (!canEditBooking()) return false;
    if (booking?.type !== "slot") return true;
    return isMemberAnAdmin(member) || isMemberAPracticeManager(member);
  };

  // R13 — slot bookings are managed by staff. Members cannot cancel them (the server returns 403
  // and the cancel dialog would only show a "managed by staff" dead-end), so hide the Cancel
  // button entirely for non-elevated users on a slot. Elevated users keep it to cancel on the
  // member's behalf.
  const canCancelBooking = (): boolean => {
    if (!canEditBooking()) return false;
    // A fully-cancelled booking can't be cancelled again — the button previously stayed visible and
    // enabled, so a re-click hit the server's "already cancelled" error. (A recurring booking cancelled
    // only from a future date is NOT isCancelled — it keeps live occurrences and remains cancellable.)
    if (booking?.isCancelled) return false;
    if (booking?.type !== "slot") return true;
    return isMemberAnAdmin(member) || isMemberAPracticeManager(member);
  };

  // Check if booking is within 24 hours of start time or has already passed
  const isWithin24HoursOrPassed = (): boolean => {
    if (!booking) return false;

    // Combine booking date and start time
    const bookingDateTime = new Date(booking.date);
    const startTimeHours = parseInt(booking.startTime.substring(0, 2), 10);
    const startTimeMinutes = parseInt(booking.startTime.substring(2, 4), 10);
    bookingDateTime.setHours(startTimeHours, startTimeMinutes, 0, 0);

    // Calculate time difference in milliseconds
    const now = new Date();
    const timeDiff = bookingDateTime.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 60 * 60);

    // Return true if booking has passed OR is within 24 hours upcoming
    return hoursDiff <= 24;
  };

  // R15/R16 — A booking has "passed" only when no upcoming, non-excluded occurrence remains.
  // For recurring bookings (adhoc or otherwise) we delegate to isRecurringActive, so a recurring
  // adhoc with future occurrences is never reported as passed even if its base date is in the past.
  const hasBookingPassed = (): boolean => {
    if (!booking) return false;

    // Slot bookings (indefinite weekly recurrence) never report as passed for cancellation purposes.
    if (booking.type === "slot") {
      return false;
    }

    // Recurring bookings (parent or instance): driven by isRecurringActive against today.
    if (booking.recurrence || booking.recurrencePattern) {
      return !isRecurringActive(booking);
    }

    // Single adhoc: passed once the calendar day is behind us.
    if (booking.type === "adhoc") {
      const bookingDate = new Date(booking.date);
      bookingDate.setHours(0, 0, 0, 0);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return bookingDate.getTime() < now.getTime();
    }

    // Single non-adhoc (weekend, trial, prospect): passed once end-time is in the past.
    const bookingDate = new Date(booking.date);
    const endTimeHours = parseInt(booking.endTime.substring(0, 2), 10);
    const endTimeMinutes = parseInt(booking.endTime.substring(2, 4), 10);
    bookingDate.setHours(endTimeHours, endTimeMinutes, 0, 0);
    return bookingDate.getTime() < new Date().getTime();
  };

  // Flex → Slot upgrade lives ONLY on the elevated (PM/admin) booking view — members can neither
  // create nor self-upgrade slots (slot requests go through a separate, off-system process), so no
  // upgrade affordance is rendered here. See PracticeBookingView for the elevated control.

  // Show loading spinner while query is loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Spinner className="size-6 mr-2" />
        <span className="text-lg">Loading booking...</span>
      </div>
    );
  }

  // Check if error is a 403 Forbidden (booking doesn't belong to member)
  const isForbiddenError = error && (error as Error & { status?: number }).status === 403;

  if (isForbiddenError) {
    return (
      <div className="flex items-center justify-center h-full min-h-32 flex-col px-4 text-center">
        <div className="border bg-gray-50 border-gray-200 rounded shadow p-4 mb-4 inline-flex items-center justify-center">
          <span className="text-lg text-center px-4">
            This booking isn't yours and therefore you cannot view its details. If you need to speak to the owner of the
            booking, contact your practice manager.
          </span>
        </div>
        <div>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  if (isError || !booking) {
    return (
      <div className="flex items-center justify-center h-32">
        <span className="text-lg">Error loading booking. Please try again.</span>
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 min-h-[80vh]">
        <div className="mb-8 mt-8">
          <Heading title={`Your Booking at ${booking.location?.name || "..."}`} />

          <span className="mt-3 text-sm text-gray-500 flex items-center justify-center gap-1">
            {canViewSensitiveInfo() && (
              <>
                Booking reference <span className="uppercase">{`${booking?.shortCode}`}</span> &middot;{" "}
              </>
            )}
            <span>
              {formatBookingDate(booking, { withTime: true })}
            </span>
          </span>
        </div>

        {booking?.isCancelled && (
          <div
            className="mb-2 p-3 bg-red-50 border-l-4 border-red-400 text-red-700 rounded-md"
            data-testid="booking-cancelled-banner"
          >
            This booking has been cancelled.
          </div>
        )}

        {!booking?.isCancelled && isBookingFullyInPast(booking) && (
          <div
            className="mb-2 p-3 bg-gray-100 border-l-4 border-gray-400 text-gray-700 rounded-md"
            data-testid="booking-ended-banner"
          >
            This booking has ended and is in the past.
          </div>
        )}

        <div className="mb-2 bg-slate-300 rounded-md p-2 w-full flex justify-between">
          <Button
            variant="link"
            onClick={() => {
              if (safeReturnUrl) {
                navigate(safeReturnUrl);
              } else {
                navigate(-1);
              }
            }}
            aria-label="Back"
            className="text-base text-slate-800 hover:text-blue-800 hover:cursor-pointer"
          >
            <ChevronLeft /> Go Back
          </Button>
          {(isMemberAnAdmin(member) || isMemberAPracticeManager(member)) && (
            <Button
              variant="secondary"
              aria-label="View booking as staff member"
              className={secondaryButtonClasses}
              onClick={() => navigate(`/location/${booking.location?.slug}/practice-bookings/${booking.id}`)}
            >
              <Eye /> <span className="hidden lg:inline">Staff View</span>
            </Button>
          )}
        </div>

        <div className="flex flex-row gap-2 lg:gap-2 mb-8 min-w-0">
          <div className="flex flex-col gap-2 basis-auto lg:basis-2/3 2xl:basis-3/4 min-w-0">
            {!canViewSensitiveInfo() && (
              <div className="p-4 mb-2 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700">
                This isn't your booking, but you can still see some details. If you need to contact the booking owner,
                you can use the information below to do so. Alternatively, contact your Practice Manager for assistance.
              </div>
            )}
            {SHOW_MEMBER_BOOKING_NOTES && canViewSensitiveInfo() && (
              <Item variant="outline" className={defaultItemClasses} data-testid="member-booking-notes">
                <ItemContent>
                  <ItemTitle className="text-base font-semibold">Your booking notes</ItemTitle>
                  <div className="text-muted-foreground font-normal text-base">
                    <Textarea
                      className="h-24 bg-white"
                      value={memberNotes}
                      onChange={(e) => setMemberNotes(e.target.value)}
                      disabled={saveState !== "idle"}
                    />
                    <p className="text-xs text-gray-500 mt-2 block px-2 w-full">
                      The Practice Rooms &amp; other members cannot see, retrieve, edit or share any notes you place
                      here. They are secure and private only to you.
                    </p>
                    <Button
                      variant={saveState === "saved" ? "default" : "default"}
                      className={`mt-4 w-full ${saveState === "saved" ? "bg-green-600 hover:bg-green-700" : ""}`}
                      onClick={handleSaveNotes}
                      disabled={!memberNotes.trim() || saveState !== "idle"}
                    >
                      <Save />
                      {saveState === "idle" && "Save"}
                      {saveState === "saving" && "Saving..."}
                      {saveState === "saved" && "Saved"}
                    </Button>
                  </div>
                </ItemContent>
              </Item>
            )}
            {canViewSensitiveInfo() && myBookingNotes.length > 0 && (
              <Item variant="outline" className={defaultItemClasses} data-testid="member-authored-note">
                <ItemContent>
                  <ItemTitle className="text-base font-semibold">Notes</ItemTitle>
                  {/* Own-authored comments only. Gated twice on purpose: the backend forces
                      authorMemberId to the caller for non-elevated members, and `myBookingNotes`
                      filters by author again so an elevated user viewing this MEMBER-FACING screen
                      never sees an admin/PM note here — those belong on the staff view. */}
                  <div className="text-muted-foreground font-normal text-base flex flex-col gap-3 mt-1">
                    {myBookingNotes.map((c) => (
                      <div key={c.id} className="flex flex-col gap-0.5">
                        <p className="whitespace-pre-line" data-testid="member-authored-note-content">
                          {c.content}
                        </p>
                        <time
                          className="text-sm text-muted-foreground/80"
                          dateTime={typeof c.createdAt === "string" ? c.createdAt : undefined}
                          data-testid="member-authored-note-timestamp"
                        >
                          {formatDateTimeVerbose(c.createdAt)}
                        </time>
                      </div>
                    ))}
                  </div>
                </ItemContent>
              </Item>
            )}
            {canViewSensitiveInfo() && (
              <div className="flex flex-row gap-2 min-w-0" data-testid="booking-reference-and-type">
                <Item variant="outline" className={`${defaultItemClasses} flex-1 basis-1/2 min-w-0`}>
                  <ItemContent>
                    <ItemTitle className="text-base font-semibold">Booking Reference</ItemTitle>
                    <ItemDescription className="text-base flex flex-col">
                      <span data-testid="booking-reference-value">{booking?.shortCode?.toUpperCase()}</span>
                    </ItemDescription>
                  </ItemContent>
                </Item>
                <Item variant="outline" className={`${defaultItemClasses} flex-1 basis-1/2 min-w-0`}>
                  <ItemContent>
                    <ItemTitle className="text-base font-semibold">Booking Type</ItemTitle>
                    <ItemDescription className="text-base flex flex-col gap-1">
                      <span data-testid="booking-type-value">{bookingTypeWellLabel(booking)}</span>
                      {booking?.isGroupBooking && (
                        <span>
                          <GroupBookingPill />
                        </span>
                      )}
                    </ItemDescription>
                  </ItemContent>
                </Item>
              </div>
            )}
            {booking.recurrence && booking.type !== "slot" && (
              <div ref={recurrenceRef}>
                <RecurrenceExceptionsManager
                  booking={booking}
                  member={member}
                  open={recurrenceOpen}
                  onOpenChange={setRecurrenceOpen}
                  onMutated={handleBookingChangeSuccess}
                />
              </div>
            )}
            {isBookingRepeating(booking) && (
              <Item variant="outline" className="bg-blue-50 border-blue-200 shadow">
                <ItemContent>
                  <div className="flex items-start gap-2">
                    <CalendarClock className="size-5 text-blue-800 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <ItemTitle className="text-base font-semibold text-blue-900">
                        {booking.recurrencePattern ? "Repeat Booking Pattern" : "Repeat Booking"}
                      </ItemTitle>
                      <div className="text-sm text-blue-800 mt-1">
                        <div className="space-y-1">
                          <p className="font-medium">
                            {/* For slots without an explicit recurrence document we use the
                                synthesised effective recurrence so the body still reads sensibly
                                (R13 — slots are always weekly). `getEffectiveRecurrence` can
                                return null for genuinely non-recurring bookings; the outer
                                isBookingRepeating guard means we never get here in that case. */}
                            {formatRecurrenceDetails(
                              booking.recurrencePattern || booking.recurrence || getEffectiveRecurrence(booking) || undefined,
                            )}
                            {booking.date && `, since ${formatGenesisDate(booking.date)}`}
                          </p>
                          {/* Bounded, non-slot series only — getRecurrenceEndDate returns null for
                              slots (indefinite) and unbounded recurrences. */}
                          {(() => {
                            // Bound the displayed end by the booking owner's membership end when
                            // they're queued for termination — prefer the booking's embedded owner,
                            // falling back to the logged-in member when they own it.
                            const owner =
                              booking.member?.id === booking.memberId
                                ? booking.member
                                : member?.id === booking.memberId
                                  ? member
                                  : undefined;
                            const membershipEnd = getMemberTerminationEndDate(owner);
                            const endDate = getRecurrenceEndDate(booking, membershipEnd);
                            // Unbounded recurrence (no count/until) rolls forward indefinitely — say so.
                            if (!endDate) {
                              return (
                                <p className="font-medium" data-testid="recurrence-end-line">
                                  No end date (ongoing)
                                </p>
                              );
                            }
                            // "Last occurrence", not "Ended": this is the final date the series is
                            // HELD, which is the week before a cancelled-from date — "Ended" read as
                            // the cancellation date.
                            return (
                              <p className="font-medium" data-testid="recurrence-end-line">
                                Last occurrence {formatGenesisDate(endDate)}
                              </p>
                            );
                          })()}
                          {booking.recurrencePattern && (
                            <p className="text-sm text-blue-800">This booking is part of a repeat series</p>
                          )}
                        </div>
                      </div>{" "}
                      {(booking.recurrence?.exceptions && booking.recurrence.exceptions.length > 0) ||
                      (booking.recurrencePattern?.exceptions && booking.recurrencePattern.exceptions.length > 0) ? (
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <div className="flex items-start gap-2">
                            <CalendarX className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-blue-900 mb-1">Excluded Dates:</p>
                              <div className="flex flex-wrap gap-2">
                                {(booking.recurrence?.exceptions || booking.recurrencePattern?.exceptions || []).map(
                                  (exception, index) => (
                                    <span
                                      key={index}
                                      className="inline-block px-2 py-1 text-xs bg-white border border-blue-300 rounded text-blue-800"
                                    >
                                      {formatUtcDate(new Date(exception))}
                                    </span>
                                  ),
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </ItemContent>
              </Item>
            )}
            {/* Consecutive Booking Chain Section — only when the merged block actually contains an
                unbookable gap (merged span longer than the hours actually booked). Once the member
                fills the gap the chain is contiguous (bookedDurationHours === totalDurationHours) and
                this "unusable gap" panel must not appear. Legacy metadata without bookedDurationHours
                falls back to showing (we can't tell, so preserve the prior behaviour). */}
            {booking?.consecutiveChainMetadata &&
              (booking.consecutiveChainMetadata.bookedDurationHours == null ||
                booking.consecutiveChainMetadata.totalDurationHours >
                  booking.consecutiveChainMetadata.bookedDurationHours) && (
              <Item variant="outline" className="bg-blue-50 border-blue-200">
                <ItemContent>
                  <div className="flex items-start gap-2">
                    <CalendarClock className="size-5 text-blue-800 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <ItemTitle className="text-base font-semibold text-blue-900">Continuous Booking Block</ItemTitle>
                      <div className="text-sm text-blue-800 mt-2 space-y-2">
                        <p className="text-black mb-8">
                          You have two bookings separated by an unusable gap. As per our policy, we've combined them
                          into a continuous block for billing purposes and you will be billed for the total duration and
                          use the room across the bookings.
                        </p>
                        <p>
                          <span className="font-medium">Individual booking time:</span>{" "}
                          {booking.startTime.replace(/^(\d{2})(\d{2})$/, "$1:$2")} -{" "}
                          {booking.endTime.replace(/^(\d{2})(\d{2})$/, "$1:$2")}
                        </p>
                        <p>
                          <span className="font-medium">Part of continuous block:</span>{" "}
                          {booking.consecutiveChainMetadata.mergedStartTime.replace(/^(\d{2})(\d{2})$/, "$1:$2")} -{" "}
                          {booking.consecutiveChainMetadata.mergedEndTime.replace(/^(\d{2})(\d{2})$/, "$1:$2")}
                        </p>
                        <p>
                          <span className="font-medium">Total charged time:</span>{" "}
                          {booking.consecutiveChainMetadata.totalDurationHours} hours
                        </p>
                        {booking.consecutiveChainMetadata.chainIds.length > 1 && (
                          <p className="text-blue-800 mt-1">
                            This is one of {booking.consecutiveChainMetadata.chainIds.length} consecutive bookings
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </ItemContent>
              </Item>
            )}
            <Item variant="outline" className={defaultItemClasses}>
              <ItemContent>
                <ItemTitle className="text-base font-semibold">Owner</ItemTitle>
                <span className="text-muted-foreground line-clamp-2 text-balance font-normal [&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4 text-base">
                  {booking?.member?.firstName} {booking?.member?.lastName}
                </span>
              </ItemContent>
            </Item>
            <Item variant="outline" className={defaultItemClasses}>
              <ItemContent>
                <ItemTitle className="text-base font-semibold">Date</ItemTitle>
                {(() => {
                  // Slot bookings (and any recurring booking) describe themselves by their
                  // recurrence pattern, not their genesis date. The genesis would mislead the
                  // operator into thinking the booking is a one-off on Dec 1 2025 when it
                  // actually fires every Monday. See lib/recurrence.ts#getEffectiveRecurrence.
                  const effectiveRecurrence = booking ? getEffectiveRecurrence(booking) : null;
                  const time = booking ? `${booking.startTime.slice(0, 2)}:${booking.startTime.slice(2)}` : "";
                  const baseClass =
                    "text-muted-foreground line-clamp-2 text-balance font-normal [&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4 text-base";

                  if (booking && effectiveRecurrence) {
                    const recurrenceLabel = formatRecurrenceDetails(effectiveRecurrence);
                    return (
                      <>
                        <span className={baseClass}>
                          {recurrenceLabel} at {time}
                        </span>
                        <span className={baseClass} data-testid="booking-next-occurrence">
                          Next booking is {humaniseBookingDate(booking)}
                        </span>
                      </>
                    );
                  }

                  return (
                    <span className={baseClass}>
                      {booking ? formatBookingDate(booking, { withDayName: true, withTime: true }) : ""}
                    </span>
                  );
                })()}
              </ItemContent>
              {canChangeTimeOrRoom() && (
                <ItemActions>
                  <DisabledFeatureTooltip disabled={!actionFlags.datetime}>
                    <Button
                      variant="outline"
                      size="sm"
                      className={secondaryButtonClasses}
                      onClick={() => {
                        setChangeType("datetime");
                        setShowChangeBookingDialog(true);
                      }}
                      disabled={hasBookingPassed() || !actionFlags.datetime}
                    >
                      Change booking date or time
                    </Button>
                  </DisabledFeatureTooltip>
                </ItemActions>
              )}
            </Item>
            <Item variant="outline" className={defaultItemClasses}>
              <ItemContent>
                <ItemTitle className="text-base font-semibold">Duration</ItemTitle>
                <ItemDescription className="text-base">
                  {booking?.startTime && booking?.endTime
                    ? humanizeDuration(booking.startTime, booking.endTime)
                    : "N/A"}
                </ItemDescription>
              </ItemContent>
              {/* Slots are a fixed 3-hour block (R13) — their duration can't be amended, so hide the button. */}
              {canChangeTimeOrRoom() && booking?.type !== "slot" && (
                <ItemActions>
                  <DisabledFeatureTooltip disabled={!actionFlags.duration}>
                    <Button
                      variant="outline"
                      size="sm"
                      className={secondaryButtonClasses}
                      onClick={() => {
                        setChangeType("duration");
                        setShowChangeBookingDialog(true);
                      }}
                      disabled={hasBookingPassed() || !actionFlags.duration}
                    >
                      Change duration
                    </Button>
                  </DisabledFeatureTooltip>
                </ItemActions>
              )}
            </Item>
            {/* {canViewSensitiveInfo() && (
              <Item variant="outline" className={defaultItemClasses}>
                <ItemContent>
                  <ItemTitle className="text-base font-semibold">Type</ItemTitle>
                  <div>
                    {booking && (
                      <div className="flex flex-row gap-2 items-center">
                        <BookingTypePill booking={booking} />
                        <Recurrent
                          recurrence={booking.recurrence}
                          recurrencePattern={booking.recurrencePattern}
                          showLabel
                        />
                      </div>
                    )}
                  </div>
                </ItemContent>
              </Item>
            )} */}
            <Item variant="outline" className={defaultItemClasses}>
              <ItemContent>
                <ItemTitle className="text-base font-semibold">Location &amp; Room</ItemTitle>
                <div className="text-muted-foreground line-clamp-2 text-balance font-normal text-base">
                  <span className="pr-2">{booking?.location?.name}</span>
                  <RoomSwatch swatch={booking?.room?.swatch ?? ""} name={booking?.room?.name ?? ""} />
                  <span className="md:hidden pl-2">{booking?.room?.name}</span>
                </div>
              </ItemContent>
              {canChangeTimeOrRoom() && (
                <ItemActions>
                  <DisabledFeatureTooltip disabled={!actionFlags.room}>
                    <Button
                      variant="outline"
                      size="sm"
                      className={secondaryButtonClasses}
                      onClick={() => {
                        setChangeType("room");
                        setShowChangeBookingDialog(true);
                      }}
                      disabled={hasBookingPassed() || !actionFlags.room}
                    >
                      Change room
                    </Button>
                  </DisabledFeatureTooltip>
                </ItemActions>
              )}
            </Item>

            {booking?.type === "slot" && booking.recurrence && (
              <SlotOccurrencesList
                booking={booking}
                canEditBooking={canEditBooking()}
                onTakeSlotHoliday={() => setShowSlotHolidayDialog(true)}
              />
            )}

            <Item variant="outline" className={defaultItemClasses}>
              <ItemContent>
                <ItemTitle className="text-base font-semibold">Address</ItemTitle>
                <ItemDescription className="text-base flex flex-col">
                  <span>{booking?.location?.address?.line1 || null}</span>
                  <span>{booking?.location?.address?.line2 || null}</span>
                  <span>{booking?.location?.address?.city || null}</span>
                  <span>{booking?.location?.address?.postcode || null}</span>
                </ItemDescription>
              </ItemContent>
            </Item>
            {(amenities.length > 0 || amenitiesLoading || (booking?.bookedAmenities && booking.bookedAmenities.length > 0)) ? (
              <Item variant="outline" className={defaultItemClasses} data-testid="booked-amenities-well">
                <ItemContent>
                  <ItemTitle className="text-base font-semibold">Booked Amenities</ItemTitle>
                  {amenitiesLoading ? (
                    <div className="text-sm text-gray-500 py-2" data-testid="booked-amenities-loading">
                      Loading amenities…
                    </div>
                  ) : amenities.length > 0 ? (
                    <div className="flex flex-row flex-wrap items-center gap-2 py-2">
                      <AmenitiesRenderer amenities={amenities} />
                    </div>
                  ) : (
                    <div
                      className="text-sm text-gray-500 py-2"
                      data-testid="booked-amenities-unresolved"
                    >
                      The amenities attached to this booking are no longer available at this location.
                    </div>
                  )}
                </ItemContent>
                {canEditBooking() && (
                  <ItemActions>
                    <Button
                      variant="outline"
                      size="sm"
                      className={secondaryButtonClasses}
                      onClick={() => setShowManageAmenitiesDialog(true)}
                      disabled={hasBookingPassed()}
                    >
                      Manage Amenities
                    </Button>
                  </ItemActions>
                )}
              </Item>
            ) : (
              canEditBooking() && !hasBookingPassed() && (
                <Item variant="outline" className={defaultItemClasses}>
                  <ItemContent>
                    <ItemTitle className="text-base font-semibold">Bookable Amenities</ItemTitle>
                    <ItemDescription className="text-base">
                      No amenities booked for this session
                    </ItemDescription>
                  </ItemContent>
                  <ItemActions>
                    <Button
                      variant="outline"
                      size="sm"
                      className={secondaryButtonClasses}
                      onClick={() => setShowManageAmenitiesDialog(true)}
                    >
                      Add Amenities
                    </Button>
                  </ItemActions>
                </Item>
              )
            )}

            {canCancelBooking() && (
              <div>
                <div className="mt-4 mb-8 w-full gap-2 flex flex-col lg:gap-2 lg:justify-center lg:items-center">
                  {booking.recurrence && booking.type !== "slot" ? (
                    <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center lg:w-2/3">
                      <Button
                        variant="outline"
                        data-testid="cancel-occurrences-button"
                        className="w-full sm:flex-1"
                        onClick={handleCancelOccurrences}
                        disabled={hasBookingPassed()}
                      >
                        Cancel occurrences
                      </Button>
                      <Button
                        variant="destructive"
                        data-testid="cancel-booking-button"
                        className="w-full sm:flex-1"
                        onClick={() => setShowCancelDialog(true)}
                        disabled={hasBookingPassed()}
                      >
                        Cancel entire booking
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="destructive"
                      data-testid="cancel-booking-button"
                      className="w-full lg:mx-auto lg:w-1/3"
                      onClick={() => setShowCancelDialog(true)}
                      disabled={hasBookingPassed()}
                    >
                      Cancel entire booking
                    </Button>
                  )}
                  {isWithin24HoursOrPassed() && !hasBookingPassed() && (
                    <p
                      data-testid="cancel-within-24h-warning"
                      className="text-sm text-gray-500 text-center px-4"
                    >
                      Bookings can be cancelled at any time, but within 24 hours of the booked time, the booking will be
                      charged for.
                    </p>
                  )}
                  {hasBookingPassed() && (
                    <p
                      data-testid="booking-ended-message"
                      className="text-sm text-red-600 text-center font-medium px-4"
                    >
                      This booking has ended and cannot be cancelled.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="hidden lg:flex lg:basis-1/3 lg:flex-col">
            {booking?.room && <RoomGallery gallery={booking.room.gallery ?? []} />}
            {booking?.room && <div className="mt-2 p-2 text-gray-700 text-sm">{booking.room.description}</div>}
          </div>
        </div>
      </div>

      {/* Slot Holiday Dialog */}
      <SlotHolidayDialog
        showDialog={showSlotHolidayDialog}
        setShowDialog={setShowSlotHolidayDialog}
        booking={booking}
        onSuccess={handleSlotHolidaySuccess}
      />

      <ChangeBookingDialog
        showBookingDialog={showChangeBookingDialog}
        setShowBookingDialog={setShowChangeBookingDialog}
        booking={booking}
        initialChangeType={changeType}
        triggerRefresh={handleBookingChangeSuccess}
      />

      <CancelBookingDialog
        booking={booking}
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
      />

      <ManageAmenitiesDialog
        booking={booking}
        isOpen={showManageAmenitiesDialog}
        onOpenChange={setShowManageAmenitiesDialog}
        onSuccess={handleBookingChangeSuccess}
      />

    </div>
  );
};

export default ViewBooking;
